import { useRef } from "react";
import CreatableSelect from "react-select/creatable";
import {
    DndContext,
    closestCenter,
    useSensor,
    useSensors,
    DragEndEvent,
    MouseSensor,
    TouchSensor,
    KeyboardSensor
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    horizontalListSortingStrategy,
    arrayMove
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    components,
    MultiValueProps,
    ControlProps,
    GroupBase
} from "react-select";

import { field, SelectOption } from "../../types";
function SortableMultiValue(
    props: MultiValueProps<SelectOption, true, GroupBase<SelectOption>>
) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition
    } = useSortable({
        id: props.data.value
    });

    return (
        <components.MultiValue
            {...props}
            innerProps = {{
                ...props.innerProps,
                ref: setNodeRef,
                ...attributes,
                ...listeners,
                style: {
                    ...(props.innerProps?.style ?? {}),
                    transform: CSS.Transform.toString(transform),
                    transition,
                    cursor: "grab"
                }
            }}
        />
    );
}
function createFocusableControl(
    selectRef: React.RefObject<any>
) {
    return function FocusableControl(
        props: ControlProps<SelectOption, true, GroupBase<SelectOption>>
    ) {
        return (
            <components.Control
                {...props}
                innerProps = {{
                    ...props.innerProps,
                    onMouseDown: e => {
                        // preserve react-select behavior
                        props.innerProps?.onMouseDown?.(e);
                        // 🔑 force focus (same effect as pressing Tab)
                        selectRef.current?.focus();
                    }
                }}
            />
        );
    };
}
export function SortableCreatableOptionsEditor({
    field,
    onChange
}: {
    field: field;
    onChange: (patch: Partial<field>) => void;
}) {
    const options = field.options ?? [];

    const selectValue = options.map(opt => ({
        label: opt,
        value: opt
    }));

    const selectRef = useRef<any>(null);

    const sensors = useSensors(
        useSensor(KeyboardSensor), 
        useSensor(MouseSensor, { activationConstraint: { distance: 5 }}),
        useSensor(TouchSensor, { activationConstraint: { delay: 400, tolerance: 5 }})
    );

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = options.findIndex(opt => opt === active.id);
        const newIndex = options.findIndex(opt => opt === over.id);
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }
        onChange({
            options: arrayMove(options, oldIndex, newIndex)
        });
    }
    const FocusableControl = createFocusableControl(selectRef);
    return (
        <div className = "my-2">
            <strong>Options</strong>
            <DndContext
                sensors = {sensors}
                collisionDetection = {closestCenter}
                onDragEnd = {handleDragEnd}
            >
                <SortableContext
                    items = {options}
                    strategy = {horizontalListSortingStrategy}
                >
                    <CreatableSelect
                        placeholder = "Type to add"
                        ref = {selectRef}
                        isMulti
                        value = {selectValue}
                        onChange = {vals => {
                            let dv = field.default_value;
                            const o = (vals ?? []).map(v => v.value);
                            if(["checkbox", "multiSelect"].includes(field.field_type)){
                                let dvs = (dv as string[]) ?? [];
                                dvs = dvs.filter(dvItem => o.includes(dvItem));
                                dv = dvs;
                            }else if(["radio", "select"].includes(field.field_type)){
                                if(!o.includes(dv as string)) dv = null;
                            }
                            onChange({
                                options: (vals ?? []).map(v => v.value),
                                default_value: dv
                            });
                            window.requestAnimationFrame(() => {
                                selectRef.current?.focus();
                            });
                        }}
                        onCreateOption = {inputValue =>{
                            onChange({
                                options: [...options, inputValue]
                            })
                            window.requestAnimationFrame(() => {
                                selectRef.current?.focus();
                            });
                        }}
                        components = {{
                            MultiValue: SortableMultiValue,
                            Control: FocusableControl
                        }}
                    />
                </SortableContext>
            </DndContext>
        </div>
    );
}
